import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, FileText, Loader2, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { SearchPicker } from "@/components/SearchPicker";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, downloadFile, errorMessage, openPdf } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { formatDate, money } from "@/lib/format";
import { PER_PAGE, Pager } from "@/components/Pager";
import { StatusBadge } from "@/components/StatusBadge";

const SCOPES = [
  { value: "firm", label: "Law firm", testId: "scope-firm" },
  { value: "client", label: "Client", testId: "scope-client" },
  { value: "contact", label: "Instructing lawyer", testId: "scope-contact" },
  { value: "case", label: "Single matter", testId: "scope-case" },
];

export default function Statements() {
  const [scope, setScope] = useState("firm");
  const [targetId, setTargetId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [options, setOptions] = useState({ firm: [], client: [], contact: [], case: [] });
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [groupPages, setGroupPages] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [firms, clients, contacts, cases] = await Promise.all([
          api.get("/firms"),
          api.get("/clients"),
          api.get("/contacts"),
          api.get("/cases"),
        ]);
        setOptions({
          firm: firms.data.map((f) => ({ id: f.firm_id, label: f.name, hint: f.city || "" })),
          client: clients.data.map((c) => ({
            id: c.client_id,
            label: c.name,
            hint: c.city || `${c.matter_count} matter(s)`,
          })),
          contact: contacts.data.map((c) => ({
            id: c.contact_id,
            label: c.full_name,
            hint: c.firm_name || c.client_name || "Direct client",
          })),
          case: cases.data.map((c) => ({
            id: c.case_id,
            label: c.title,
            hint: c.matter_ref || c.forum || "",
          })),
        });
      } catch (error) {
        toast.error(errorMessage(error, "Could not load accounts"));
      }
    })();
  }, []);

  const list = options[scope] || [];

  useEffect(() => {
    setTargetId("");
    setStatement(null);
  }, [scope]);

  const params = useMemo(() => {
    const query = { scope, target_id: targetId };
    if (dateFrom) query.date_from = dateFrom;
    if (dateTo) query.date_to = dateTo;
    return query;
  }, [scope, targetId, dateFrom, dateTo]);

  const queryString = useMemo(
    () => new URLSearchParams(params).toString(),
    [params],
  );

  const load = useCallback(async () => {
    if (!targetId) return;
    // scope tabs reset the selection a tick later — never fetch with an id from the old scope
    if (!list.some((o) => o.id === targetId)) return;
    setLoading(true);
    try {
      const { data } = await api.get("/statements/preview", { params });
      setStatement(data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not build the statement"));
      setStatement(null);
    } finally {
      setLoading(false);
    }
  }, [params, targetId, list]);

  useEffect(() => {
    load();
  }, [load]);

  const symbol = statement?.currency_symbol || "Rs.";

  const act = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (error) {
      toast.error(errorMessage(error, "Action failed"));
    } finally {
      setBusy("");
    }
  };

  const sendEmail = () =>
    act("email", async () => {
      const { data } = await api.post("/statements/send-email", {
        scope,
        target_id: targetId,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      });
      toast.success(`Statement emailed to ${data.recipient}`);
    });

  const sendWhatsApp = () =>
    act("whatsapp", async () => {
      const { data } = await api.post("/statements/whatsapp", {
        scope,
        target_id: targetId,
        date_from: dateFrom || null,
        date_to: dateTo || null,
      });
      openWhatsApp(data);
    });

  return (
    <div className="space-y-8" data-testid="statements-page">
      <header>
        <p className="label-caps">Accounts</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
          Statement of account
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          One sendable statement for a law firm, an individual instructing lawyer or a single
          matter — every invoice, what has been received against it and the running balance,
          sub-grouped by the contact person who briefed the work.
        </p>
      </header>

      <div className="surface p-5">
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-sm">
            {SCOPES.map((s) => (
              <TabsTrigger
                key={s.value}
                value={s.value}
                className="rounded-sm"
                data-testid={s.testId}
              >
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="mt-5 grid gap-4 lg:grid-cols-4">
          <div className="space-y-2 lg:col-span-2">
            <Label className="label-caps">Account</Label>
            <SearchPicker
              items={list}
              value={targetId}
              onSelect={setTargetId}
              placeholder={`Start typing a ${SCOPES.find((s) => s.value === scope).label.toLowerCase()} name…`}
              testId="statement-target"
              emptyLabel="Nothing matches that text."
            />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">From (invoice date)</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="num rounded-sm"
              data-testid="statement-date-from"
            />
          </div>
          <div className="space-y-2">
            <Label className="label-caps">To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="num rounded-sm"
              data-testid="statement-date-to"
            />
          </div>
        </div>

        {(dateFrom || dateTo) && (
          <button
            type="button"
            onClick={() => {
              setDateFrom("");
              setDateTo("");
            }}
            data-testid="statement-clear-dates"
            className="mt-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 transition-colors duration-150 hover:text-slate-900"
          >
            Clear date range
          </button>
        )}
      </div>

      {!targetId ? (
        <div className="surface hairline-grid p-10 text-center" data-testid="statement-empty">
          <FileText className="mx-auto h-6 w-6 text-slate-300" strokeWidth={1.4} />
          <p className="mt-4 text-sm text-slate-600">
            Choose an account above to build its statement.
          </p>
        </div>
      ) : loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="statement-loading">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : statement ? (
        <div className="space-y-6" data-testid="statement-preview">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-serif text-2xl text-slate-900">{statement.account.name}</h2>
              <p className="mt-1 text-[12px] text-slate-500">
                {statement.account.subtitle}
                {" · as at "}
                {formatDate(statement.period.as_at)}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 no-print">
              <button
                type="button"
                onClick={() => openPdf(`/statements/pdf?${queryString}`)}
                data-testid="statement-preview-pdf"
                className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
              >
                <FileText className="h-3.5 w-3.5" strokeWidth={1.8} />
                Preview PDF
              </button>
              <button
                type="button"
                onClick={() =>
                  downloadFile(`/statements/pdf?${queryString}`, "statement_of_account.pdf")
                }
                data-testid="statement-download-pdf"
                className="inline-flex items-center gap-2 border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
              >
                <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
                Download
              </button>
              <button
                type="button"
                onClick={sendEmail}
                disabled={busy === "email"}
                data-testid="statement-send-email"
                className="inline-flex items-center gap-2 bg-slate-900 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-60"
              >
                {busy === "email" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Mail className="h-3.5 w-3.5" strokeWidth={1.8} />
                )}
                Email statement
              </button>
              <button
                type="button"
                onClick={sendWhatsApp}
                disabled={busy === "whatsapp"}
                data-testid="statement-send-whatsapp"
                className="inline-flex items-center gap-2 bg-emerald-700 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-emerald-800 disabled:opacity-60"
              >
                {busy === "whatsapp" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <MessageCircle className="h-3.5 w-3.5" strokeWidth={1.8} />
                )}
                WhatsApp
              </button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { label: "Billed", value: statement.totals.billed, testId: "statement-billed" },
              { label: "Received", value: statement.totals.received, testId: "statement-received" },
              {
                label: "Outstanding",
                value: statement.totals.outstanding,
                testId: "statement-outstanding",
              },
              { label: "Of which overdue", value: statement.totals.overdue, testId: "statement-overdue" },
            ].map((k) => (
              <div key={k.label} className="surface min-w-0 p-5" data-testid={k.testId}>
                <p className="label-caps">{k.label}</p>
                <p className="num mt-3 text-xl tracking-tight text-slate-900">
                  {money(k.value, symbol)}
                </p>
              </div>
            ))}
          </div>

          {statement.groups.length === 0 ? (
            <div className="surface p-10 text-center" data-testid="statement-no-rows">
              <p className="text-sm text-slate-600">
                No invoices for this account in the chosen period.
              </p>
            </div>
          ) : (
            statement.groups.map((group) => (
              <section key={group.contact_id} data-testid={`statement-group-${group.contact_id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-slate-200 pb-3">
                  <div>
                    <h3 className="font-serif text-lg text-slate-900">{group.name}</h3>
                    <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">
                      {[group.designation, group.firm_name].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <p className="num text-[13px] text-slate-900">
                    {money(group.outstanding, symbol)}{" "}
                    <span className="text-[11px] uppercase tracking-[0.12em] text-slate-400">
                      outstanding
                    </span>
                  </p>
                </div>
                <div className="min-w-0 overflow-x-auto border-x border-b border-slate-200 bg-white">
                  <table className="w-full min-w-[760px] text-left">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        {["Date", "Invoice", "Matter", "Billed", "Received", "Balance", "Status"].map(
                          (h) => (
                            <th
                              key={h}
                              className={`label-caps px-4 py-2.5 ${
                                ["Billed", "Received", "Balance"].includes(h) ? "text-right" : ""
                              }`}
                            >
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {group.rows
                        .slice(0, (groupPages[group.contact_id] || 1) * PER_PAGE)
                        .map((row) => (
                        <tr key={row.invoice_id} data-testid={`statement-row-${row.invoice_number}`}>
                          <td className="num px-4 py-3 text-[12px] text-slate-600">
                            {formatDate(row.issue_date)}
                          </td>
                          <td className="num px-4 py-3 text-[12px] text-slate-900">
                            {row.invoice_number}
                          </td>
                          <td className="max-w-[260px] px-4 py-3">
                            <p className="truncate text-[13px] text-slate-800">{row.case_title}</p>
                            <p className="truncate text-[11px] text-slate-400">
                              {[row.matter_ref, row.forum].filter(Boolean).join(" · ")}
                            </p>
                          </td>
                          <td className="num px-4 py-3 text-right text-[12px] text-slate-800">
                            {money(row.total, symbol)}
                          </td>
                          <td className="num px-4 py-3 text-right text-[12px] text-emerald-700">
                            {money(row.received, symbol)}
                          </td>
                          <td className="num px-4 py-3 text-right text-[13px] font-semibold text-slate-900">
                            {money(row.running_balance, symbol)}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={row.display_status} />
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50">
                        <td colSpan={3} className="px-4 py-3 text-[12px] font-semibold text-slate-700">
                          Sub-total
                        </td>
                        <td className="num px-4 py-3 text-right text-[12px] font-semibold text-slate-900">
                          {money(group.billed, symbol)}
                        </td>
                        <td className="num px-4 py-3 text-right text-[12px] font-semibold text-emerald-700">
                          {money(group.received, symbol)}
                        </td>
                        <td className="num px-4 py-3 text-right text-[13px] font-semibold text-slate-900">
                          {money(group.outstanding, symbol)}
                        </td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>
                {group.rows.length > (groupPages[group.contact_id] || 1) * PER_PAGE ? (
                  <button
                    type="button"
                    onClick={() =>
                      setGroupPages((p) => ({
                        ...p,
                        [group.contact_id]: (p[group.contact_id] || 1) + 1,
                      }))
                    }
                    data-testid={`statement-group-more-${group.contact_id}`}
                    className="mt-3 border border-slate-300 bg-white px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600 transition-colors duration-150 hover:border-slate-900 hover:text-slate-900"
                  >
                    Show more fee notes ·{" "}
                    {group.rows.length - (groupPages[group.contact_id] || 1) * PER_PAGE} left
                  </button>
                ) : null}
              </section>
            ))
          )}

          <div className="surface min-w-0 overflow-x-auto p-5" data-testid="statement-aging">
            <p className="label-caps">Ageing of outstanding</p>
            <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-5">
              {statement.aging.map((b) => (
                <div key={b.bucket}>
                  <p className="text-[11px] text-slate-500">{b.label}</p>
                  <p className="num mt-1 text-[13px] text-slate-900">{money(b.amount, symbol)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
